import {
  EditorState,
  Modifier,
  SelectionState,
  convertFromRaw,
  convertToRaw,
  genKey,
} from 'draft-js';
import { translateVariable } from '../components/RichEditor/utils';

// Valeurs prédéfinies d'un champ de texte : contrairement à celles d'une
// variable texte custom (chaîne en base), ce sont des contenus Draft
// sérialisés, portés par la box. Elles peuvent donc contenir des variables et
// des images, comme n'importe quel contenu du document.

export const parseRawContent = (value) => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  try {
    const raw = JSON.parse(value);
    if (!raw || !Array.isArray(raw.blocks)) return null;
    return raw;
  } catch (e) {
    // Contenu illisible (saisie corrompue, format d'une version antérieure) :
    // ignoré plutôt que de casser l'application de toute la sélection.
    return null;
  }
};

export const rawContentToText = (value) => {
  const raw = parseRawContent(value);
  if (!raw) return '';
  return raw.blocks.map((block) => block.text).join('\n').trim();
};

export const rawContentHasEntity = (value, entityType) => {
  const raw = parseRawContent(value);
  if (!raw) return false;
  return Object.values(raw.entityMap ?? {}).some((entity) => entity?.type === entityType);
};

// Éditeur réellement vide : un seul bloc, sans texte ni entité. Un bloc sans
// texte mais porteur d'une image n'est pas vide.
const isEmptyRawContent = (raw) =>
  raw.blocks.length === 1 &&
  !raw.blocks[0].text.trim() &&
  (raw.blocks[0].entityRanges ?? []).length === 0;

export const isEditorStateEmpty = (editorState) => {
  if (!editorState) return true;
  return isEmptyRawContent(convertToRaw(editorState.getCurrentContent()));
};

// Le texte figé dans une valeur prédéfinie est celui qu'affichait l'éditeur au
// moment de sa saisie : côté modèle, les variables n'ont pas de valeur, c'est
// donc leur *nom* qui est stocké. Sans cette passe, une valeur appliquée dans
// une proposition afficherait ce nom jusqu'au prochain chargement du document,
// où replaceVariables le réécrit. On refait ici le même travail, borné au
// contenu inséré.
// Plages des entités variable d'un contenu. Balayage local plutôt que le
// getEntities de l'éditeur : ce module est chargé par des utilitaires, importer
// le module de l'éditeur y ferait entrer tout l'arbre de composants.
const findVariableEntities = (editorState) => {
  const content = editorState.getCurrentContent();
  const found = [];
  content.getBlocksAsArray().forEach((block) => {
    let currentEntityKey = null;
    block.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        if (!entityKey) return false;
        if (content.getEntity(entityKey).getType() !== 'VARIABLE') return false;
        currentEntityKey = entityKey;
        return true;
      },
      (start, end) => {
        found.push({
          entityKey: currentEntityKey,
          entity: content.getEntity(currentEntityKey),
          blockKey: block.getKey(),
          start,
          end,
        });
      }
    );
  });
  return found;
};

export const hydrateVariables = ({ editorState, variables, isTemplate = false }) => {
  if (!editorState || !variables) return editorState;

  // Plages traitées de la dernière à la première dans un bloc : réécrire une
  // plage décale celles qui la suivent.
  const entities = findVariableEntities(editorState).sort((a, b) =>
    a.blockKey === b.blockKey ? b.start - a.start : 0
  );
  if (entities.length === 0) return editorState;

  let content = editorState.getCurrentContent();
  let nextEditorState = editorState;

  entities.forEach(({ entityKey, blockKey, start, end, entity }) => {
    const { key, lineStyle, separator } = entity.getData();
    // Variable absente du document (valeur héritée d'un autre modèle) : on
    // laisse le texte tel quel plutôt que de vider l'emplacement.
    if (!variables[key]) return;

    const text = translateVariable({ variableKey: key, variables, isTemplate, lineStyle, separator });
    if (typeof text !== 'string' || text === '') return;

    content = Modifier.replaceText(
      content,
      SelectionState.createEmpty(blockKey).merge({ anchorOffset: start, focusOffset: end }),
      text,
      content.getBlockForKey(blockKey).getInlineStyleAt(start),
      entityKey
    );
    nextEditorState = EditorState.push(nextEditorState, content, 'insert-characters');
  });

  return nextEditorState;
};

// Concatène des contenus sérialisés à la suite du contenu courant. Les clés
// d'entités sont réindexées : chaque valeur prédéfinie numérote les siennes à
// partir de zéro, les coller telles quelles ferait pointer les variables et
// images des unes sur les entités des autres.
export const appendRawContents = ({ editorState, rawValues }) => {
  if (!editorState) return null;
  const parsedValues = (rawValues ?? []).map(parseRawContent).filter(Boolean);
  if (parsedValues.length === 0) return null;

  const base = convertToRaw(editorState.getCurrentContent());
  // Champ encore vide : on remplace son bloc vide plutôt que de pousser la
  // valeur une ligne plus bas.
  const blocks = isEmptyRawContent(base) ? [] : [...base.blocks];
  const entityMap = { ...(base.entityMap ?? {}) };
  let entityCounter = Object.keys(entityMap).length;

  parsedValues.forEach((raw) => {
    const remappedKeys = {};
    Object.keys(raw.entityMap ?? {}).forEach((oldKey) => {
      const newKey = String(entityCounter);
      entityCounter += 1;
      entityMap[newKey] = raw.entityMap[oldKey];
      remappedKeys[oldKey] = Number(newKey);
    });
    raw.blocks.forEach((block) => {
      blocks.push({
        ...block,
        // Clé neuve : deux applications de la même valeur cohabiteraient sinon
        // avec des clés de blocs identiques.
        key: genKey(),
        entityRanges: (block.entityRanges ?? []).map((range) => ({
          ...range,
          key: remappedKeys[range.key],
        })),
      });
    });
  });

  if (blocks.length === 0) return null;

  return EditorState.push(
    editorState,
    convertFromRaw({ blocks, entityMap }),
    'insert-fragment'
  );
};
